import { useState, useEffect } from "react";
import { api } from "../lib/api";
import { formatApiError } from "../context/AuthContext";
import { toast } from "sonner";
import { SOURCE_LABELS, TARGET_LABELS } from "../lib/constants";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Plus, Loader2 } from "lucide-react";

const EMPTY = { nome: "", cognome: "", phone: "", email: "", indirizzo_residenza: "", source: "pubblicita", target: "residenziale", notes: "", assigned_to: "", partner_commerciale_id: "", entity_type: "persona_fisica", ragione_sociale: "", partita_iva: "", codice_fiscale: "" };

export default function NewLeadDialog({ onCreated, trigger }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [users, setUsers] = useState([]);

  useEffect(() => {
    if (open) api.get("/users").then((r) => setUsers(r.data || [])).catch(() => setUsers([]));
  }, [open]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.nome.trim()) { toast.error("Il nome è obbligatorio"); return; }
    if (form.entity_type === "azienda") {
      if (!form.ragione_sociale.trim()) { toast.error("Inserisci la ragione sociale"); return; }
      if (form.partita_iva.trim() && !/^\d{11}$/.test(form.partita_iva.replace(/\s/g, ""))) { toast.error("La partita IVA non è valida (11 cifre)"); return; }
    }
    if (!form.assigned_to) { toast.error("Seleziona il referente assegnato"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/leads", form);
      toast.success("Lead registrato con successo");
      setForm(EMPTY);
      setOpen(false);
      onCreated?.(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button data-testid="open-new-lead-btn" className="font-semibold">
            <Plus className="h-4 w-4 mr-1.5" /> Nuovo Lead
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg bg-popover max-h-[90dvh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading">Registra nuovo lead</DialogTitle>
          <DialogDescription className="sr-only">Compila i dati del nuovo lead</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-4 py-2">
          <div className="col-span-2 space-y-1.5">
            <Label className="text-xs uppercase tracking-widest text-muted-foreground">Tipo soggetto *</Label>
            <Select value={form.entity_type} onValueChange={(v) => set("entity_type", v)}>
              <SelectTrigger data-testid="lead-entity-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="persona_fisica">Persona fisica</SelectItem>
                <SelectItem value="azienda">Azienda</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {form.entity_type === "azienda" ? (
            <>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Ragione sociale *</Label>
                <Input value={form.ragione_sociale} onChange={(e) => set("ragione_sociale", e.target.value)} data-testid="lead-ragione-sociale-input" placeholder="Es. Rossi Impianti Srl" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Partita IVA</Label>
                <Input value={form.partita_iva} onChange={(e) => set("partita_iva", e.target.value)} data-testid="lead-partita-iva-input" placeholder="IT01234567890" />
              </div>
            </>
          ) : (
            <div className="col-span-2 space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Codice fiscale <span className="normal-case text-muted-foreground/70">(opzionale)</span></Label>
              <Input value={form.codice_fiscale} onChange={(e) => set("codice_fiscale", e.target.value)} data-testid="lead-codice-fiscale-input" placeholder="RSSMRA80A01H501U" />
            </div>
          )}
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-widest text-muted-foreground">Nome *</Label>
            <Input value={form.nome} onChange={(e) => set("nome", e.target.value)} data-testid="lead-nome-input" placeholder="Mario" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-widest text-muted-foreground">Cognome</Label>
            <Input value={form.cognome} onChange={(e) => set("cognome", e.target.value)} data-testid="lead-cognome-input" placeholder="Rossi" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-widest text-muted-foreground">Telefono</Label>
            <Input value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="lead-phone-input" placeholder="+39 ..." />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-widest text-muted-foreground">Email</Label>
            <Input value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="lead-email-input" />
          </div>
          <div className="col-span-2 space-y-1.5">
            <Label className="text-xs uppercase tracking-widest text-muted-foreground">Indirizzo di residenza</Label>
            <Input value={form.indirizzo_residenza} onChange={(e) => set("indirizzo_residenza", e.target.value)} data-testid="lead-address-input" placeholder="Via Roma 12, 25100 Brescia" />
            <p className="text-[10px] text-muted-foreground">La città viene rilevata automaticamente dall'indirizzo.</p>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-widest text-muted-foreground">Origine</Label>
            <Select value={form.source} onValueChange={(v) => set("source", v)}>
              <SelectTrigger data-testid="lead-source-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(SOURCE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-widest text-muted-foreground">Target</Label>
            <Select value={form.target} onValueChange={(v) => set("target", v)}>
              <SelectTrigger data-testid="lead-target-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(TARGET_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2 space-y-1.5">
            <Label className="text-xs uppercase tracking-widest text-muted-foreground">Referente assegnato *</Label>
            <Select value={form.assigned_to} onValueChange={(v) => set("assigned_to", v)}>
              <SelectTrigger data-testid="lead-assigned-select"><SelectValue placeholder="Seleziona un referente" /></SelectTrigger>
              <SelectContent>
                {users.filter((u) => u.role !== "admin").map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <p className="text-[10px] text-muted-foreground">Ogni lead deve avere un referente. Potrai modificarlo in seguito.</p>
          </div>
          <div className="col-span-2 space-y-1.5">
            <Label className="text-xs uppercase tracking-widest text-muted-foreground">Partner commerciale <span className="normal-case text-muted-foreground/70">(opzionale)</span></Label>
            <Select value={form.partner_commerciale_id || "none"} onValueChange={(v) => set("partner_commerciale_id", v === "none" ? "" : v)}>
              <SelectTrigger data-testid="lead-partner-comm-select"><SelectValue placeholder="Nessuno" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Nessuno</SelectItem>
                {users.filter((u) => u.role === "partner_commerciale").map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <p className="text-[10px] text-muted-foreground">Se selezionato, provvigione e marginalità del partner guideranno il prezzo suggerito.</p>
          </div>
          <div className="col-span-2 space-y-1.5">
            <Label className="text-xs uppercase tracking-widest text-muted-foreground">Note</Label>
            <Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} data-testid="lead-notes-input" rows={2} />
          </div>
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={saving} data-testid="save-lead-btn" className="font-semibold">
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Registra lead
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
